/**
 * DealsBadgeContext — web port of the RN context.
 *
 * Tracks "new" deals granted via workflow rules. Uses `visibilitychange`
 * for refresh-on-foreground in place of RN's AppState.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import dealsApi from '../api/dealsApi';
import { useAuth } from '../hooks/useAuth';
import { useUserContext } from './UserContext';
import logger from '../utils/logger';

export interface DealsBadgeState {
  newDealsCount: number;
  newDealIds: string[];
  loading: boolean;
  error: string | null;
  lastRefreshed: Date | null;
}

export interface DealsBadgeContextValue extends DealsBadgeState {
  refresh: () => Promise<void>;
  markSeen: (dealIds?: string[]) => Promise<void>;
  markAllSeen: () => Promise<void>;
}

const DealsBadgeContext = createContext<DealsBadgeContextValue | null>(null);

interface DealsBadgeProviderProps {
  children: ReactNode;
  refreshInterval?: number;
}

export function DealsBadgeProvider({
  children,
  refreshInterval = 60000,
}: DealsBadgeProviderProps) {
  const { isAuthenticated } = useAuth();
  const { user } = useUserContext();
  const isMounted = useRef(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<DealsBadgeState>({
    newDealsCount: 0,
    newDealIds: [],
    loading: false,
    error: null,
    lastRefreshed: null,
  });

  const fetchNewDealsCount = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const data = await dealsApi.getNewDealsCount();
      if (!isMounted.current) return;
      const { count, deal_ids } = data;
      setState(prev => ({
        ...prev,
        newDealsCount: count,
        newDealIds: deal_ids || [],
        loading: false,
        lastRefreshed: new Date(),
      }));
    } catch (error) {
      if (!isMounted.current) return;
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch new deals count';
      setState(prev => ({ ...prev, loading: false, error: message }));
      logger.error('[DealsBadge] Failed to fetch new deals count', error);
    }
  }, [isAuthenticated, user?.id]);

  const markSeen = useCallback(
    async (dealIds?: string[]) => {
      if (!isAuthenticated || !user?.id) return;
      try {
        await dealsApi.markDealsSeen({
          deal_ids: dealIds,
          all: !dealIds,
        });
        if (dealIds) {
          setState(prev => ({
            ...prev,
            newDealsCount: Math.max(0, prev.newDealsCount - dealIds.length),
            newDealIds: prev.newDealIds.filter(id => !dealIds.includes(id)),
          }));
        } else {
          setState(prev => ({ ...prev, newDealsCount: 0, newDealIds: [] }));
        }
      } catch (error) {
        logger.error('[DealsBadge] Failed to mark deals as seen', error);
        fetchNewDealsCount();
      }
    },
    [isAuthenticated, user?.id, fetchNewDealsCount],
  );

  const markAllSeen = useCallback(async () => {
    await markSeen(undefined);
  }, [markSeen]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchNewDealsCount();
    } else {
      setState({
        newDealsCount: 0,
        newDealIds: [],
        loading: false,
        error: null,
        lastRefreshed: null,
      });
    }
  }, [isAuthenticated, user?.id, fetchNewDealsCount]);

  useEffect(() => {
    if (refreshInterval > 0 && isAuthenticated) {
      refreshTimerRef.current = setInterval(
        fetchNewDealsCount,
        refreshInterval,
      );
      return () => {
        if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      };
    }
  }, [refreshInterval, isAuthenticated, fetchNewDealsCount]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchNewDealsCount();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAuthenticated, fetchNewDealsCount]);

  const value: DealsBadgeContextValue = {
    ...state,
    refresh: fetchNewDealsCount,
    markSeen,
    markAllSeen,
  };

  return (
    <DealsBadgeContext.Provider value={value}>
      {children}
    </DealsBadgeContext.Provider>
  );
}

export function useDealsBadge(): DealsBadgeContextValue {
  const ctx = useContext(DealsBadgeContext);
  if (!ctx)
    throw new Error('useDealsBadge must be used within a DealsBadgeProvider');
  return ctx;
}

export default DealsBadgeContext;
